import { Country } from 'types/userManagement'

export type ContextType = 'asset' | 'product'

// AI-supported languages (all system languages are supported)
export const AI_SUPPORTED_LANGUAGES = {
  pl: 'Polish',
  uk: 'Ukrainian',
  et: 'Estonian',
  lv: 'Latvian',
  lt: 'Lithuanian',
  sl: 'Slovenian',
  sk: 'Slovak',
  cs: 'Czech',
  hr: 'Croatian',
  sr: 'Serbian',
  ro: 'Romanian',
  hu: 'Hungarian'
} as const

// Map SindyCat countries to AI-supported languages - all native languages supported
export const AI_COUNTRY_LANGUAGE_MAPPING = {
  [Country.Poland]: 'pl', // Polish -> Polish AI
  [Country.Ukraine]: 'uk', // Ukrainian -> Ukrainian AI
  [Country.Estonia]: 'et', // Estonian -> Estonian AI
  [Country.Latvia]: 'lv', // Latvian -> Latvian AI
  [Country.Lithuania]: 'lt', // Lithuanian -> Lithuanian AI
  [Country.Slovenia]: 'sl', // Slovenian -> Slovenian AI
  [Country.Slovakia]: 'sk', // Slovak -> Slovak AI
  [Country.CzechRepublic]: 'cs', // Czech -> Czech AI
  [Country.Croatia]: 'hr', // Croatian -> Croatian AI
  [Country.Serbia]: 'sr', // Serbian -> Serbian AI
  [Country.Romania]: 'ro', // Romanian -> Romanian AI
  [Country.Hungary]: 'hu' // Hungarian -> Hungarian AI
} as const

export type Language = keyof typeof AI_SUPPORTED_LANGUAGES

export interface ImageConfig {
  id: string
  source: 'existing' | 'upload' | 'url'
  imageUrl?: string
  file?: File
  useForGeneration: boolean // Flagging for other fields
  assignedToField?: string // Dedicated field
  filename?: string
  mimeType?: string
  preview?: string // URL for preview
}

export interface FieldConfig {
  fieldName: string
  isSelected: boolean
  requiresImage: boolean
  currentValue?: string // For regeneration
  dedicatedImages: ImageConfig[]
  userInstructions?: string // Field-specific instructions
}

export interface GenerateContentPayload {
  sessionId?: string
  contextType: ContextType
  /** Product asset: use with assetType. Do not send when landingPageAssetId is set. */
  assetId?: string
  assetType?: string
  productId?: string
  /** Backend fills from LP asset when landingPageAssetId is set. Do not send for LP assets. */
  brandId?: string
  countryCode?: string
  language: Language
  contextId?: string
  productCategory?: string
  /** LP edit: existing row. Do not combine with landingPageAssetTypeId or product-asset fields. */
  landingPageAssetId?: string
  /** LP create (no row yet): with brandId, countryCode, productCategory. Do not combine with landingPageAssetId. */
  landingPageAssetTypeId?: string
  fieldsToGenerate: Array<{
    fieldName: string
    currentValue?: string
    images?: Array<{
      imageUrl?: string
      imageBuffer?: string
      filename?: string
      mimeType?: string
      useForGeneration?: boolean
      isVideo?: boolean // If true, this is a video file, not an image
    }>
    userInstructions?: string // Field-specific instructions
    replaceInstructions?: boolean // If true, replace existing instructions. If false/undefined, append.
    uploadedFileIndices?: number[]
  }>
  existingContent?: Record<string, string>
  userInstructions?: string
}

export interface ConfirmGenerationPayload {
  sessionId: string
  fieldsToAccept?: string[]
}

export interface GenerationResult {
  success: boolean
  sessionId: string
  generatedContent: Record<string, string>
  limitsConsumed?: boolean
  remainingRegenerations?: number
  partialFailures?: Array<{
    field: string
    error: string
  }>
  message?: string
  tokens?: {
    promptTokens: number
    completionTokens: number
    totalTokens: number
    cachedTokens: number
    operationTokens: number
  }
  operationNumber?: number
}

export interface ConfirmationResult {
  success: boolean
  message: string
}

export interface GenerationRecord {
  timestamp: string
  fields: string[]
  generatedContent: Record<string, string>
  success: boolean
  limitsConsumed?: boolean
  error?: string
}

export interface SessionHistory {
  sessionId: string
  contextType: ContextType
  contextId?: string
  assetType?: string
  language: Language
  generations: GenerationRecord[]
  confirmed: boolean
  createdAt: string
  updatedAt: string
  expiresAt: string
}

export interface AiAssistantProps {
  // Context usage
  context: ContextType
  contextId?: string // ID of edited asset/product (product asset). Omit when landingPageAssetId is set.

  // LP edit: backend loads LP asset and fills context (do not send brandId/country/productCategory/existingContent)
  landingPageAssetId?: string
  // LP create: new asset row — same as form; requires brandId, country, productCategory
  landingPageAssetTypeId?: string

  // Context data for AI (required for LP create and product assets; omitted for LP edit)
  assetType?: string
  brandId: string
  brandName?: string // Add brand name for field validation
  country: string
  productCategory?: string
  namedContextId?: string

  // Callback for applying suggestions
  onSuggestionApply: (suggestions: Record<string, string>) => void
  onImagesApply?: (images: Array<{ fieldName: string; file: File }>) => void // Cal lback for uploaded images
  onSessionUpdate?: (sessionId: string) => void
  onConfirmationSuccess?: () => void // Callback called after successful confirmation
  onFinishGeneration?: () => void // Callback called after generation is finished (saves and opens edit view)

  // Visibility control
  isVisible: boolean
  onToggle: (visible: boolean) => void

  // Session and regeneration
  existingSessionId?: string // For session continuation
  existingContent?: Record<string, string> // Current field values

  // Available images (for existing assets/products)
  availableImages?: Array<{
    url: string
    alt?: string
    fieldName?: string
    file?: File
  }>
  
  // Available videos (for existing assets/products)
  availableVideos?: Array<{
    url: string
    alt?: string
    fieldName?: string
    file?: File
  }>

  // Brand-specific fields (for assets/products with custom fields like descriptionForTIM)
  brandSpecificFields?: Array<{
    name: string
    label: string
    type?: string
  }>
}

export interface AiAssistantState {
  // Loading status
  isLoading: boolean
  error: string | null

  // Session
  sessionId: string | null
  sessionHistory: SessionHistory | null
  regenerationCount: number
  remainingRegenerations: number

  // Configuration
  selectedFields: FieldConfig[]
  additionalContext: string
  selectedLanguage: Language

  // Images
  imageConfigs: ImageConfig[]

  // Results
  generatedContent: Record<string, string>
  suggestions: Record<string, string>
  isConfirmed: boolean

  // Brand-specific fields for label resolution (for assets/products with custom fields like descriptionForTIM)
  brandSpecificFields: Array<{ name: string; label: string; type?: string }>

  // Initial generation state for configuration summary
  initialGenerationFields: string[]
  initialGenerationImageCount: number

  // Field management
  removedFields: Set<string>
  removedFromGeneration: Set<string> // Fields removed from generation in results view
  fieldContexts: Record<string, string>
  namedContextId: string | null | undefined
}

export interface SessionData {
  sessionId: string
  contextType: ContextType
  assetId?: string
  assetType?: string
  productId?: string
  /** Set when session is for an existing LP asset (from backend). */
  landingPageAssetId?: string
  /** Set when session is for LP create flow until the row is saved. */
  landingPageAssetTypeId?: string
  language: Language
  generatedContent: Record<string, string>
  existingContent?: Record<string, string>
  userInstructions?: string // Shared instructions
  fieldUserInstructions?: Record<string, string> // Field-specific instructions
  imageFieldMapping?: {
    fieldSpecificImages?: Record<string, string[]>
    additionalContextImages?: string[]
  }
  processedImages?: any[]
  createdAt: string
  updatedAt: string
  expiresAt: string
}

export interface UseAiAssistantReturn {
  state: AiAssistantState
  actions: {
    // Configuration
    updateAdditionalContext: (context: string) => void
    toggleFieldSelection: (fieldName: string) => void
    updateFieldCurrentValue: (fieldName: string, value: string) => void
    updateFieldInstructions: (
      fieldName: string,
      instructions: string,
      replace?: boolean
    ) => void

    // Field management
    removeField: (fieldName: string) => void
    addField: (fieldName: string) => void
    removeFromGeneration: (fieldName: string) => void
    restoreToGeneration: (fieldName: string) => void
    updateFieldContext: (fieldName: string, context: string) => void
    updateNamedContextId: (contextId: string | null) => void

    // Generation
    generateContent: () => Promise<void>
    regenerateContent: () => Promise<void>
    regenerateSingleField: (
      fieldName: string,
      editedValue?: string
    ) => Promise<void>
    confirmGeneration: (fieldsToAccept?: string[]) => Promise<void>

    // Session
    clearSession: () => void
    loadSessionData: (sessionId: string) => Promise<void>

    // Suggestions
    applySuggestion: (fieldName: string, value: string) => void
    applyAllSuggestions: () => void
    dismissSuggestion: (fieldName: string) => void

    // Reset
    reset: () => void
  }
}
